// Hook específico para operações de usuários, usando useRequest
import { useRequest } from './useRequest';
import { UserFull, UserCreateApi } from '../types/user';
import { useCallback } from 'react';
import { useCurrentUser } from '../contexts/UserContext';

export function useUsers() {
  const usersRequest = useRequest<UserFull[]>();
  const saveUserRequest = useRequest<UserFull>();
  const deleteUserRequest = useRequest<void>();
  const { currentUser, loading: userLoading } = useCurrentUser();

  // Função para converter UserFull para o formato da API
  const convertToUserApi = useCallback((userData: UserFull, empresaId?: string): UserCreateApi => {
    return {
      ...(userData.id && { id: userData.id }),
      email: userData.email,
      razaoSocial: userData.razaoSocial,
      cpfCnpj: userData.cpfCnpj,
      inscricaoEstadual: userData.inscricaoEstadual,
      inscricaoMunicipal: userData.inscricaoMunicipal,
      tipo: userData.tipo,
      plano: userData.plano || 1, // Valor padrão se não definido
      ativo: userData.ativo,
      excluido: userData.excluido,
      empresaId: empresaId || userData.empresaId || '',
      telefones: userData.telefones.map(phone => ({
        tipo: phone.tipo,
        ddd: phone.ddd,
        numero: phone.numero,
        observacao: phone.observacao
      }))
    };
  }, []);

  // Buscar todos os usuários
  const fetchUsers = useCallback(() => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/usuarios`;
    usersRequest.execute(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Criar novo usuário
  const createUser = useCallback(async (userData: UserFull) => {
    if (userLoading || !currentUser) {
      throw new Error('Aguarde o carregamento das informações do usuário...');
    }
    const empresaId = currentUser?.id || userData.empresaId;
    if (!empresaId) {
      throw new Error('Empresa ID não encontrado. Usuário deve estar logado.');
    }

    const userApiData = convertToUserApi(userData, empresaId);

    await saveUserRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/empresas/${empresaId}/usuarios`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userApiData),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, convertToUserApi, userLoading]);

  // Atualizar usuário existente
  const updateUser = useCallback(async (userData: UserFull) => {
    const userApiData = convertToUserApi(userData);

    await saveUserRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/usuarios/${userData.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userApiData),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertToUserApi]);

  // Deletar usuário
  const deleteUser = useCallback(async (id: number) => {
    await deleteUserRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/usuarios/${id}`, {
      method: 'DELETE',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Salvar usuário (cria ou atualiza baseado na presença do ID)
  const saveUser = useCallback(async (userData: UserFull) => {
    if (userData.id) {
      await updateUser(userData);
    } else {
      // Para criação, usar o empresaId do usuário autenticado
      if (userLoading || !currentUser) {
        throw new Error('Aguarde o carregamento das informações do usuário...');
      }
      const empresaId = currentUser?.id;
      if (!empresaId) {
        throw new Error('Empresa ID não encontrado. Usuário deve estar logado para criar usuários.');
      }
      await createUser({ ...userData, empresaId });
    }
  }, [createUser, updateUser, currentUser, userLoading]);

  return {
    // Estados para listagem
    users: usersRequest.data,
    usersLoading: usersRequest.loading,
    usersError: usersRequest.error,

    // Estados para operações de salvar
    saveLoading: saveUserRequest.loading,
    saveError: saveUserRequest.error,

    // Estados para operações de deletar
    deleteLoading: deleteUserRequest.loading,
    deleteError: deleteUserRequest.error,

    // Estado do usuário atual
    userLoading,

    // Funções
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    saveUser,
  };
}